import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw, X } from "lucide-react";

import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError, apiFetch } from "@/lib/api";
import {
  MRP_INVENTORY_STOCK_PHYSICAL_AVAILABLE_SHORT_HINT,
  MRP_LOCATION_PUBLIC_LOCATION_ID,
  allocateNowQuantityValidationIssue,
  formatMrpInventoryVerifiedAge,
  mrpInventoryBomLineBindsShelfAllocateCap,
  mrpInventorySetVerifiedAsOf,
  mrpInventoryStockPhysicalAvailable,
  mrpWorkOrderMaxAllocateQuantity,
  type MrpInventoryListResponse,
  type MrpInventoryRow,
  type MrpInventorySkusRefreshResponse,
  type MrpLocation,
} from "@/lib/mrp";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { cn } from "@/lib/utils";
import { FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import {
  MANUFACTURING_HUB_READ_ONLY,
  formatBomEachQuantity,
  formatCount,
  formatDateTime,
  isHubNativeWorkOrder,
  type WorkOrderDetail,
} from "./workOrdersPageModel";
import { workOrderDetailQueryKey } from "./workOrderDetailWorkspace";

export type PartialMaterialAllocation = { sku: string; quantity: number };

interface MvComponentAllocationLine {
  componentSku: string;
  orderedQuantity: number;
  allocatedQuantity: number;
}

interface ReceiveQuantityCapResponse {
  maxAdditionalReceive: number;
  orderedFg: number;
  receivedFg: number;
  maxFromOrdered: number;
  maxFromAllocations: number;
  bottleneckComponentSku: string | null;
  mirrorSynced?: boolean;
  capBlockReason?: "fully_received" | "insufficient_allocation" | "failed_receipt_needs_repair" | null;
  blockingFailedReceipts?: Array<{ receiptId: number; status: string; mvWorkOrderNo: string | null }>;
}

export function ReceiveFinishedGoodModal({
  workOrder,
  buildOrderId,
  onClose,
  onReceive,
  isPending,
  error,
}: {
  workOrder: WorkOrderDetail;
  buildOrderId: number;
  onClose: () => void;
  onReceive: (quantity: number, close: boolean) => void;
  isPending: boolean;
  error: ApiError | null;
}) {
  const queryClient = useQueryClient();
  const [receiveQuantity, setReceiveQuantity] = useState("0");
  const [closeWhenShort, setCloseWhenShort] = useState(false);

  const capQuery = useQuery<ReceiveQuantityCapResponse, ApiError>({
    queryKey: ["manufacturing", "work-orders", "receive-cap", buildOrderId],
    queryFn: () =>
      apiFetch<ReceiveQuantityCapResponse>(`/api/manufacturing/work-orders/${buildOrderId}/receive-quantity-cap`),
    enabled: buildOrderId > 0,
    staleTime: 5_000,
  });

  const cap = capQuery.data;
  const hubNative = isHubNativeWorkOrder(workOrder);

  useEffect(() => {
    if (!cap?.mirrorSynced) return;
    void queryClient.invalidateQueries({ queryKey: workOrderDetailQueryKey(buildOrderId) });
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders"] });
  }, [buildOrderId, cap?.mirrorSynced, queryClient]);

  useEffect(() => {
    if (!cap) return;
    const max = cap.maxAdditionalReceive;
    setReceiveQuantity(max > 0 ? String(max) : "0");
    setCloseWhenShort(false);
  }, [buildOrderId, cap?.maxAdditionalReceive]);

  const capLoading = capQuery.isLoading;
  const capError = capQuery.error ?? null;
  const maxAdditionalReceive = cap?.maxAdditionalReceive ?? 0;
  const maxFromOrdered = cap?.maxFromOrdered ?? 0;
  const maxFromAllocations = cap?.maxFromAllocations ?? 0;
  const parsedReceiveQuantity = Math.trunc(Number(receiveQuantity));
  const receiveQty =
    Number.isFinite(parsedReceiveQuantity) && parsedReceiveQuantity > 0
      ? Math.min(parsedReceiveQuantity, maxAdditionalReceive > 0 ? maxAdditionalReceive : parsedReceiveQuantity)
      : 0;
  const completesOrderedQuantity = maxFromOrdered > 0 && receiveQty >= maxFromOrdered;
  const materialsRemainBeyondReceive =
    !completesOrderedQuantity && closeWhenShort && maxFromAllocations > receiveQty && receiveQty > 0;
  const willClose = completesOrderedQuantity || closeWhenShort;
  const receiveDisabled =
    isPending ||
    capLoading ||
    capError != null ||
    cap == null ||
    maxAdditionalReceive <= 0 ||
    materialsRemainBeyondReceive;

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="receive-fg-modal-title"
    >
      <Card className="relative my-4 w-full max-w-md shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <h3 id="receive-fg-modal-title" className="text-base font-semibold text-slate-900">
              Receive finished good
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close receive finished good"
              disabled={isPending}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Work order <span className="font-mono">{workOrder.mv_number ?? workOrder.number}</span>
          </p>
          <p className="text-xs text-slate-500">
            {hubNative
              ? "Hub-native receive posts inventory and accounting from the hub BOM. Partial receives leave the work order open."
              : "Megaventory creates the work-order finished-goods receipt (WOFG) when you receive. Partial receives leave the work order open so labor can still be updated."}
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32">
              <Label htmlFor="receive-fg-qty">Quantity</Label>
              <Input
                id="receive-fg-qty"
                type="number"
                min={maxAdditionalReceive > 0 ? 1 : 0}
                max={maxAdditionalReceive > 0 ? maxAdditionalReceive : undefined}
                step="1"
                value={receiveQuantity}
                onChange={(event) => setReceiveQuantity(event.target.value)}
                disabled={isPending || capLoading || cap == null}
              />
            </div>
            {completesOrderedQuantity ? (
              <p className="pb-0.5 text-sm text-slate-700">
                Closes after receive — this completes the ordered quantity.
              </p>
            ) : (
              <label className="flex items-center gap-2 pb-0.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={closeWhenShort}
                  onChange={(event) => setCloseWhenShort(event.target.checked)}
                  disabled={isPending}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Done — close after this lesser receive
              </label>
            )}
          </div>
          {materialsRemainBeyondReceive ? (
            <p className="text-xs text-amber-800">
              Allocated materials still cover more than this receive. Return the excess materials first, then receive
              and close.
            </p>
          ) : null}
          {cap && closeWhenShort && !completesOrderedQuantity && !materialsRemainBeyondReceive && receiveQty > 0 ? (
            <p className="text-xs text-slate-600">
              This closes the work order short of the ordered quantity. Confirm no further units will be received on
              this work order.
            </p>
          ) : null}
          {cap && cap.bottleneckComponentSku != null ? (
            <p className="text-xs text-slate-600">
              Allocated materials limit how many finished goods you can receive before the next allocation (tightest
              line: <span className="font-mono">{cap.bottleneckComponentSku}</span>, up to{" "}
              <span className="tabular-nums">{cap.maxFromAllocations}</span> from stock on hand for this work order).
            </p>
          ) : null}
          {cap && maxAdditionalReceive <= 0 && capError == null ? (
            <p className="text-xs text-amber-800">
              {cap.capBlockReason === "fully_received" ? (
                <>
                  {hubNative ? "This work order already shows" : "Megaventory already shows"}{" "}
                  <span className="tabular-nums">{cap.receivedFg}</span> of{" "}
                  <span className="tabular-nums">{cap.orderedFg}</span> received — nothing left to receive.
                  {!hubNative && cap.mirrorSynced ? " The hub mirror was refreshed from Megaventory." : null}
                </>
              ) : cap.capBlockReason === "insufficient_allocation" ? (
                <>Allocated materials do not cover another finished good. Allocate more on the BOM before receiving.</>
              ) : cap.capBlockReason === "failed_receipt_needs_repair" ? (
                <>
                  A previous receive created a Megaventory work order
                  {cap.blockingFailedReceipts != null && cap.blockingFailedReceipts.length > 0
                    ? ` (${cap.blockingFailedReceipts
                        .map((row) => row.mvWorkOrderNo)
                        .filter((label): label is string => label != null && label.trim() !== "")
                        .join(", ")})`
                    : ""}{" "}
                  but did not finish posting on the hub. Repair that receipt before receiving again. A support ticket
                  was opened for the failed receive.
                </>
              ) : (
                <>
                  Nothing left to receive for the ordered quantity, or allocated materials do not cover another unit.
                </>
              )}
            </p>
          ) : null}
          {capError ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {capError.message}
            </div>
          ) : null}
          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error.message}
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
            <Button type="button" variant="ghost" className="shrink-0 whitespace-nowrap" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              className="shrink-0 whitespace-nowrap"
              onClick={() => {
                if (!cap) return;
                let numeric = Math.trunc(Number(receiveQuantity));
                if (!Number.isFinite(numeric) || numeric <= 0) return;
                numeric = Math.min(numeric, cap.maxAdditionalReceive);
                if (numeric <= 0) return;
                const completes = cap.maxFromOrdered > 0 && numeric >= cap.maxFromOrdered;
                onReceive(numeric, completes || closeWhenShort);
              }}
              isLoading={isPending}
              disabled={receiveDisabled}
            >
              {willClose ? "Receive and close" : "Receive"}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

export function ReturnMaterialsModal({
  workOrder,
  buildOrderId,
  onClose,
  onReturn,
  isPending,
  error,
}: {
  workOrder: WorkOrderDetail;
  buildOrderId: number;
  onClose: () => void;
  onReturn: (returns: PartialMaterialAllocation[]) => void;
  isPending: boolean;
  error: ApiError | null;
}) {
  const allocationsQuery = useQuery<{ lines: MvComponentAllocationLine[] }, ApiError>({
    queryKey: ["manufacturing", "work-orders", "mv-allocations", buildOrderId],
    queryFn: () =>
      apiFetch<{ lines: MvComponentAllocationLine[] }>(
        `/api/manufacturing/work-orders/${buildOrderId}/mv-component-allocations`,
      ),
    staleTime: 10_000,
  });

  const [quantitiesBySku, setQuantitiesBySku] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const line of allocationsQuery.data?.lines ?? []) {
      next[line.componentSku] = "0";
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }, [allocationsQuery.data]);

  function setReturnQuantityForSku(sku: string, value: string): void {
    setQuantitiesBySku((current) => ({ ...current, [sku]: value }));
    setValidationError(null);
  }

  function setReturnInputsToAllocated(): void {
    const next: Record<string, string> = {};
    for (const line of allocationsQuery.data?.lines ?? []) {
      next[line.componentSku] = String(Math.max(0, line.allocatedQuantity));
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }

  function setReturnInputsToZero(): void {
    const next: Record<string, string> = {};
    for (const line of allocationsQuery.data?.lines ?? []) {
      next[line.componentSku] = "0";
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }

  function buildReturns(): PartialMaterialAllocation[] | null {
    const out: PartialMaterialAllocation[] = [];
    for (const line of allocationsQuery.data?.lines ?? []) {
      const raw = quantitiesBySku[line.componentSku] ?? "";
      const trimmed = raw.trim();
      if (trimmed === "") continue;
      const numeric = Number(trimmed);
      if (!Number.isFinite(numeric)) {
        setValidationError(`Invalid quantity for ${line.componentSku}: use a whole number, 0, or leave blank to skip.`);
        return null;
      }
      if (numeric < 0) {
        setValidationError(`Quantity for ${line.componentSku} cannot be negative.`);
        return null;
      }
      if (numeric === 0) {
        continue;
      }
      if (!Number.isInteger(numeric)) {
        setValidationError(`Quantity for ${line.componentSku} must be a whole number.`);
        return null;
      }
      if (numeric > line.allocatedQuantity + 1e-9) {
        setValidationError(
          `Quantity for ${line.componentSku} cannot exceed allocated (${line.allocatedQuantity}).`,
        );
        return null;
      }
      out.push({ sku: line.componentSku, quantity: numeric });
    }
    if (out.length === 0) {
      setValidationError("Enter a positive return quantity on at least one line, or use Set to allocated.");
      return null;
    }
    return out;
  }

  function handleReturn(): void {
    const returns = buildReturns();
    if (returns == null) return;
    onReturn(returns);
  }

  const lines = allocationsQuery.data?.lines ?? [];
  const linesWithAllocation = lines.filter((line) => line.allocatedQuantity > 0);

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="return-materials-modal-title"
    >
      <Card className="relative my-4 w-full max-w-2xl shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <h3 id="return-materials-modal-title" className="text-base font-semibold text-slate-900">
              Return allocated material
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close return materials"
              disabled={isPending}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Work order <span className="font-mono">{workOrder.mv_number ?? workOrder.number}</span> — quantities are
            checked against Megaventory allocated amounts.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {allocationsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading allocation state from Megaventory…</p>
          ) : allocationsQuery.error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {allocationsQuery.error.message}
            </div>
          ) : linesWithAllocation.length === 0 ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">No allocated component quantity to return on this work order.</p>
              <Button type="button" variant="ghost" className="self-end sm:self-auto" onClick={onClose} disabled={isPending}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={setReturnInputsToAllocated} disabled={isPending}>
                  Set to allocated
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={setReturnInputsToZero} disabled={isPending}>
                  Set to 0
                </Button>
              </div>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Component</th>
                      <th className="px-3 py-2 text-right">Ordered</th>
                      <th className="px-3 py-2 text-right">Allocated</th>
                      <th className="px-3 py-2 text-right">Return now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {linesWithAllocation.map((line) => (
                      <tr key={line.componentSku}>
                        <td className="px-3 py-2 font-mono text-xs">{line.componentSku}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCount(line.orderedQuantity)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCount(line.allocatedQuantity)}</td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            className="h-8 w-24 text-right font-mono text-xs"
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={quantitiesBySku[line.componentSku] ?? ""}
                            onChange={(event) => setReturnQuantityForSku(line.componentSku, event.target.value)}
                            disabled={isPending}
                            aria-label={`Return quantity for ${line.componentSku}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleReturn} isLoading={isPending} disabled={isPending}>
                  Return entered quantities
                </Button>
              </div>
            </>
          )}

          {validationError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {validationError}
            </div>
          ) : null}
          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error.message}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

/** Shown when hovering the Available column label in Allocate materials. */
const ALLOCATE_MODAL_AVAILABLE_TOOLTIP = MRP_INVENTORY_STOCK_PHYSICAL_AVAILABLE_SHORT_HINT;
const ALLOCATE_MODAL_AVAILABLE_SERVICE_TOOLTIP =
  "Service components are not stock-backed. Available does not apply; Allocate now uses remaining work order demand.";
const ALLOCATE_MODAL_TOTAL_TOOLTIP =
  "Total component quantity needed by this work order.";
const ALLOCATE_MODAL_ALLOCATED_TOOLTIP =
  "Amount already allocated to this work order only.";
const ALLOCATE_MODAL_REMAINING_TOOLTIP =
  "Still needed on this work order (total minus allocated on this work order).";
const ALLOCATE_MODAL_QTY_PER_FG_TOOLTIP =
  "Component quantity required to build one finished good on this work order.";

function normalizeAllocateInventoryLocationCode(code: string): string {
  return code.trim().toLowerCase();
}

function inventoryRowsByNormalizedCpn(rows: MrpInventoryRow[]): Map<string, MrpInventoryRow> {
  const map = new Map<string, MrpInventoryRow>();
  for (const row of rows) {
    map.set(String(row.cpn).trim().toLowerCase(), row);
  }
  return map;
}

export function AllocateMaterialsModal({
  workOrder,
  onClose,
  onAllocatePartial,
  isPending,
  error,
  hubWritesAllowed,
}: {
  workOrder: WorkOrderDetail;
  onClose: () => void;
  onAllocatePartial: (allocations: PartialMaterialAllocation[]) => void;
  isPending: boolean;
  error: ApiError | null;
  hubWritesAllowed: boolean;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [quantitiesBySku, setQuantitiesBySku] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const siteCode =
    workOrder.mrp_site_code != null && workOrder.mrp_site_code.trim() !== ""
      ? workOrder.mrp_site_code.trim()
      : workOrder.bom[0]?.locationCode != null && workOrder.bom[0].locationCode.trim() !== ""
        ? workOrder.bom[0].locationCode.trim()
        : null;

  const componentSkus = useMemo(
    () =>
      [...new Set(workOrder.bom.map((line) => line.componentSku.trim()).filter((sku) => sku.length > 0))],
    [workOrder.bom],
  );

  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    componentSkus,
    "work-order-allocate-modal",
  );

  const inventoryQuery = useQuery<MrpInventoryListResponse, ApiError>({
    queryKey: ["mrp", "inventory", "allocate-modal", siteCode ?? "", componentSkus.join("\n")],
    queryFn: async () => {
      if (siteCode == null || componentSkus.length === 0) {
        return { rows: [], inventory_last_full_sync_at: null };
      }
      const siteKey = normalizeAllocateInventoryLocationCode(siteCode);
      const response = await apiFetch<MrpInventoryListResponse>("/api/mrp/inventory", {
        query: { cpns: componentSkus.join(","), location: siteCode },
      });
      return {
        rows: response.rows.filter(
          (row) => normalizeAllocateInventoryLocationCode(row.location_code) === siteKey,
        ),
        inventory_last_full_sync_at: response.inventory_last_full_sync_at,
      };
    },
    enabled: siteCode != null && componentSkus.length > 0,
    staleTime: 15_000,
  });

  const refreshSnapshotMutation = useMutation<MrpInventorySkusRefreshResponse, ApiError>({
    mutationFn: async () => {
      if (componentSkus.length === 0) {
        throw new ApiError(400, "No component SKUs on this work order to refresh.");
      }
      const result = await apiFetch<MrpInventorySkusRefreshResponse>("/api/mrp/inventory/refresh-skus", {
        method: "POST",
        body: {
          cpns: componentSkus,
          force: true,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["mrp", "inventory", "allocate-modal"] });
      await queryClient.refetchQueries({ queryKey: ["mrp", "inventory", "allocate-modal"] });
      return result;
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const isSnapshotRefreshing = refreshSnapshotMutation.isPending;

  const inventoryByCpn = useMemo(
    () => inventoryRowsByNormalizedCpn(inventoryQuery.data?.rows ?? []),
    [inventoryQuery.data?.rows],
  );

  const snapshotAsOf = useMemo(
    () =>
      mrpInventorySetVerifiedAsOf(
        componentSkus,
        inventoryQuery.data?.rows ?? [],
        inventoryQuery.data?.inventory_last_full_sync_at,
      ),
    [
      componentSkus,
      inventoryQuery.data?.rows,
      inventoryQuery.data?.inventory_last_full_sync_at,
    ],
  );

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const line of workOrder.bom) {
      const inv = inventoryByCpn.get(line.componentSku.trim().toLowerCase());
      const max = mrpWorkOrderMaxAllocateQuantity(line, inv);
      next[line.componentSku] = String(max);
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }, [
    workOrder.id,
    workOrder.bom.map((line) => `${line.id}:${line.quantityRemaining}:${line.quantityAllocated}`).join("|"),
    inventoryByCpn,
  ]);

  const allocateLineIssues = useMemo(() => {
    const issues = new Map<string, string>();
    for (const line of workOrder.bom) {
      const issue = allocateNowQuantityValidationIssue({
        componentSku: line.componentSku,
        rawQuantity: quantitiesBySku[line.componentSku] ?? "",
        quantityRemaining: line.quantityRemaining,
        quantityAllocated: line.quantityAllocated,
        mvProductType: line.mvProductType,
        inventoryRow: inventoryByCpn.get(line.componentSku.trim().toLowerCase()),
      });
      if (issue != null) {
        issues.set(line.componentSku, issue);
      }
    }
    return issues;
  }, [workOrder.bom, quantitiesBySku, inventoryByCpn]);

  const allocateBlockedByQuantityCap = allocateLineIssues.size > 0;

  function availableNumericForSku(componentSku: string): number | null {
    const inv = inventoryByCpn.get(componentSku.trim().toLowerCase());
    if (inv == null) return null;
    return mrpInventoryStockPhysicalAvailable(inv);
  }

  function maxAllocateNumericForLine(line: WorkOrderDetail["bom"][number]): number {
    const inv = inventoryByCpn.get(line.componentSku.trim().toLowerCase());
    return mrpWorkOrderMaxAllocateQuantity(line, inv);
  }

  /** Sets each Allocate now field to the shared max (Available shelf cap and BOM remaining). */
  function setAllocateInputsToMax(): void {
    const next: Record<string, string> = {};
    for (const line of workOrder.bom) {
      next[line.componentSku] = String(maxAllocateNumericForLine(line));
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }

  function setAllocateInputsToZero(): void {
    const next: Record<string, string> = {};
    for (const line of workOrder.bom) {
      next[line.componentSku] = "0";
    }
    setQuantitiesBySku(next);
    setValidationError(null);
  }

  function setQuantityForSku(sku: string, value: string): void {
    setQuantitiesBySku((current) => ({ ...current, [sku]: value }));
    setValidationError(null);
  }

  function buildPartialAllocations(): PartialMaterialAllocation[] | null {
    if (allocateBlockedByQuantityCap) {
      return null;
    }
    const out: PartialMaterialAllocation[] = [];
    for (const line of workOrder.bom) {
      const raw = quantitiesBySku[line.componentSku] ?? "";
      const trimmed = raw.trim();
      if (trimmed === "") continue;
      const issue = allocateNowQuantityValidationIssue({
        componentSku: line.componentSku,
        rawQuantity: raw,
        quantityRemaining: line.quantityRemaining,
        quantityAllocated: line.quantityAllocated,
        mvProductType: line.mvProductType,
        inventoryRow: inventoryByCpn.get(line.componentSku.trim().toLowerCase()),
      });
      if (issue != null) {
        return null;
      }
      const numeric = Number(trimmed);
      if (numeric === 0) {
        continue;
      }
      out.push({ sku: line.componentSku, quantity: numeric });
    }
    if (out.length === 0) {
      setValidationError(
        "Enter a positive quantity on at least one line, or use Set to on max so at least one line is greater than zero.",
      );
      return null;
    }
    return out;
  }

  function handleAllocatePartial(): void {
    const allocations = buildPartialAllocations();
    if (allocations == null) return;
    onAllocatePartial(allocations);
  }

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="allocate-modal-title"
    >
      <Card className="relative my-4 w-full max-w-5xl shadow-xl">
        <CardHeader>
          <h3 id="allocate-modal-title" className="text-base font-semibold text-slate-900">
            Allocate materials (<span className="font-mono">{workOrder.mv_number ?? workOrder.number}</span>)
          </h3>
          {siteCode != null ? (
            <div className="mt-4 flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                <span className="font-medium text-slate-800">Manufacturing site</span>
                {(() => {
                  const siteKey = siteCode.trim().toLowerCase();
                  const knownSiteId =
                    siteKey in MRP_LOCATION_PUBLIC_LOCATION_ID
                      ? MRP_LOCATION_PUBLIC_LOCATION_ID[siteKey as MrpLocation]
                      : workOrder.location_id != null && workOrder.location_id > 0
                        ? workOrder.location_id
                        : null;
                  if (knownSiteId != null) {
                    return (
                      <ManufacturingLocationChip
                        locationId={knownSiteId}
                        fallbackLabel={siteCode}
                      />
                    );
                  }
                  return <span className="font-mono text-slate-900">{siteCode}</span>;
                })()}
                {snapshotAsOf != null ? (
                  <span
                    className="text-xs text-slate-500"
                    title={`Verified as of ${formatDateTime(snapshotAsOf.toISOString())}`}
                  >
                    Inventory verified {formatMrpInventoryVerifiedAge(snapshotAsOf)}
                  </span>
                ) : inventoryQuery.isSuccess ? (
                  <span className="text-xs text-slate-500">
                    No inventory verify time available yet.
                  </span>
                ) : null}
              </div>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="shrink-0"
                  onClick={() => refreshSnapshotMutation.mutate()}
                  disabled={!hubWritesAllowed || isPending || isSnapshotRefreshing}
                  aria-busy={isSnapshotRefreshing}
                >
                  <RefreshCw
                    className={cn("h-4 w-4", isSnapshotRefreshing && "animate-spin")}
                    aria-hidden
                  />
                  Reload inventory snapshot
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          {workOrder.bom.length === 0 ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">No BOM lines with remaining demand.</p>
              <Button type="button" variant="ghost" className="self-end sm:self-auto" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                  <HoverHintTrigger
                    portalZIndex={FLOATING_POPOVER_Z_INDEX}
                    trigger={
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={setAllocateInputsToMax}
                        disabled={!hubWritesAllowed || isPending || inventoryQuery.isLoading}
                      >
                        Set to on max
                      </Button>
                    }
                  >
                    Fills each Allocate now field with the smaller of work order demand and shelf availability. Service lines use remaining demand only (no shelf cap).
                  </HoverHintTrigger>
                </ReadOnlyHoverLayer>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                  <HoverHintTrigger
                    portalZIndex={FLOATING_POPOVER_Z_INDEX}
                    trigger={
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={setAllocateInputsToZero}
                        disabled={!hubWritesAllowed || isPending}
                      >
                        Set to 0
                      </Button>
                    }
                  >
                    Sets every Allocate now field to 0 (those lines are skipped when you allocate).
                  </HoverHintTrigger>
                </ReadOnlyHoverLayer>
              </div>

              {inventoryQuery.error ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  Could not load MRP inventory snapshot: {inventoryQuery.error.message}
                </div>
              ) : null}

              <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Component</th>
                      <th className="px-3 py-2 text-right">
                        <HoverHintContent
                          hoverHint={ALLOCATE_MODAL_QTY_PER_FG_TOOLTIP}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        >
                          <span className="cursor-help border-b border-dotted border-slate-400">Qty per FG</span>
                        </HoverHintContent>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <HoverHintContent
                          hoverHint={ALLOCATE_MODAL_TOTAL_TOOLTIP}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        >
                          <span className="cursor-help border-b border-dotted border-slate-400">Total</span>
                        </HoverHintContent>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <HoverHintContent
                          hoverHint={ALLOCATE_MODAL_ALLOCATED_TOOLTIP}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        >
                          <span className="cursor-help border-b border-dotted border-slate-400">Allocated</span>
                        </HoverHintContent>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <HoverHintContent
                          hoverHint={ALLOCATE_MODAL_REMAINING_TOOLTIP}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        >
                          <span className="cursor-help border-b border-dotted border-slate-400">
                            Remaining to allocate
                          </span>
                        </HoverHintContent>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <HoverHintContent
                          hoverHint={ALLOCATE_MODAL_AVAILABLE_TOOLTIP}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        >
                          <span className="cursor-help border-b border-dotted border-slate-400">Available</span>
                        </HoverHintContent>
                      </th>
                      <th className="px-3 py-2 text-right">Allocate now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {workOrder.bom.map((line) => {
                      const bindsShelfCap = mrpInventoryBomLineBindsShelfAllocateCap(line.mvProductType);
                      const inv = inventoryByCpn.get(line.componentSku.trim().toLowerCase());
                      const availableValue = inv != null ? availableNumericForSku(line.componentSku) : null;
                      const available = !bindsShelfCap
                        ? "service"
                        : availableValue != null
                          ? formatCount(availableValue)
                          : inventoryQuery.isLoading
                            ? "…"
                            : "—";
                      return (
                        <tr key={line.id}>
                          <td className="max-w-[14rem] px-3 py-2">
                            <MrpSkuHoverPeek
                              sku={line.componentSku}
                              hint={skuHoverHints?.[line.componentSku.trim()]}
                              hintsLoading={hintsLoading}
                              onHoverIntent={enableHintsFetch}
                              tab="inventory"
                              className="text-xs"
                              linkClassName="text-xs no-underline hover:text-sky-950"
                              portalZIndex={FLOATING_POPOVER_Z_INDEX}
                            />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                            {formatBomEachQuantity(line.quantityPerFinishedGood)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                            {formatCount(line.quantityOrdered)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                            {formatCount(line.quantityAllocated)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                            {formatCount(line.quantityRemaining)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                            {!bindsShelfCap ? (
                              <HoverHintContent
                                hoverHint={ALLOCATE_MODAL_AVAILABLE_SERVICE_TOOLTIP}
                                portalZIndex={FLOATING_POPOVER_Z_INDEX}
                              >
                                <span className="cursor-help border-b border-dotted border-slate-400 text-slate-600">
                                  {available}
                                </span>
                              </HoverHintContent>
                            ) : (
                              available
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const lineIssue = allocateLineIssues.get(line.componentSku) ?? null;
                              const input = (
                                <Input
                                  className={cn(
                                    "h-8 w-24 text-right font-mono text-xs",
                                    lineIssue != null &&
                                      "border-red-400 focus-visible:ring-red-400",
                                  )}
                                  type="number"
                                  min="0"
                                  step="1"
                                  inputMode="numeric"
                                  value={quantitiesBySku[line.componentSku] ?? ""}
                                  onChange={(event) =>
                                    setQuantityForSku(line.componentSku, event.target.value)
                                  }
                                  disabled={!hubWritesAllowed || isPending}
                                  aria-invalid={lineIssue != null}
                                  aria-label={`Allocate quantity for ${line.componentSku}`}
                                />
                              );
                              if (lineIssue == null) {
                                return input;
                              }
                              return (
                                <HoverHintTrigger
                                  portalZIndex={FLOATING_POPOVER_Z_INDEX}
                                  align="center"
                                  trigger={input}
                                >
                                  {lineIssue}
                                </HoverHintTrigger>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {validationError && !allocateBlockedByQuantityCap ? (
                  <span className="mr-auto min-w-0 max-w-full text-sm text-amber-900">
                    {validationError}
                  </span>
                ) : null}
                <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint={MANUFACTURING_HUB_READ_ONLY}
                  layout="inline"
                >
                  <Button
                    type="button"
                    variant={allocateBlockedByQuantityCap ? "danger" : "primary"}
                    onClick={handleAllocatePartial}
                    isLoading={isPending}
                    disabled={
                      !hubWritesAllowed || isPending || allocateBlockedByQuantityCap
                    }
                  >
                    {allocateBlockedByQuantityCap
                      ? "Error: over-allocated"
                      : "Allocate entered quantities"}
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
            </>
          )}

          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {error.message}
            </div>
          ) : null}

        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

export function WorkOrderCancelModal({
  open,
  detail,
  hubWritesAllowed,
  isPending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  detail: WorkOrderDetail;
  hubWritesAllowed: boolean;
  isPending: boolean;
  error: ApiError | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}): JSX.Element | null {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open, detail.id]);

  if (!open) {
    return null;
  }

  const woLabel = detail.mv_number ?? detail.number;
  const reasonTrimmed = reason.trim();
  const confirmDisabled = !hubWritesAllowed || isPending || reasonTrimmed === "";

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="work-order-cancel-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isPending) {
          onClose();
        }
      }}
    >
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="flex flex-row items-start justify-between gap-3 border-b border-slate-100">
          <div>
            <h2 id="work-order-cancel-title" className="text-lg font-semibold text-slate-900">
              Cancel work order?
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              This marks <span className="font-medium text-slate-900">{woLabel}</span> cancelled in the hub. Soft-allocated
              materials are returned automatically. This cannot be undone.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
            aria-label="Close"
            disabled={isPending}
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="work-order-cancel-reason">Cancellation reason</Label>
            <TextArea
              id="work-order-cancel-reason"
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={!hubWritesAllowed || isPending}
              placeholder="For example: duplicate work order created by mistake"
            />
          </div>
          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              {error.message}
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
              Keep work order
            </Button>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
              <Button
                type="button"
                variant="danger"
                isLoading={isPending}
                disabled={confirmDisabled}
                onClick={() => onConfirm(reasonTrimmed)}
              >
                Cancel work order
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}